import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Field, Modal, Notice } from '@/components/Modal';
import { apiFetch } from '@/lib/api/client';
import { NasAdminAccountSection } from './NasAdminAccountSection';
import {
  useNasTest,
  useNasWrite,
  type NasConnection,
  type NasConnectionInput,
} from './useDocuments';

const PROTOCOLS = [
  { value: 'SYNOLOGY_WEBAPI', label: 'Synology (QuickConnect)' },
  { value: 'SMB', label: 'SMB (사설망 공유폴더)' },
  { value: 'WEBDAV', label: 'WebDAV (준비중)' },
];

const EMPTY: NasConnectionInput = {
  name: '', siteId: null, departmentId: null, protocol: 'SYNOLOGY_WEBAPI', host: '', port: null,
  basePath: '/', username: '', password: '', domain: null,
};

/**
 * 프로토콜마다 <b>같은 칸이 다른 것을 뜻한다.</b> 한 벌의 라벨로 두면 SMB 를 고른 사람이 호스트 칸에
 * QuickConnect ID 를 넣으라는 안내를 보게 되고, 시작 경로가 공유폴더 이름이라는 사실은 어디에도 안 적힌다
 * — 그러면 저장은 되고 연결 테스트만 실패한다(NAS_SHARE_NOT_FOUND).
 */
type ProtocolHint = {
  host: string;
  hostPlaceholder: string;
  basePath: string;
  basePathPlaceholder: string;
  note: string;
};

/**
 * 모르는 프로토콜이 왔을 때 물러설 자리. <b>따로 상수로 뽑아 둔다</b> —
 * `HINTS.SYNOLOGY_WEBAPI` 로 물러서면 그것도 인덱스 접근이라 `noUncheckedIndexedAccess` 아래에서
 * 여전히 `undefined` 일 수 있는 값이 되고, 화면이 그것을 쓰는 다섯 자리가 전부 컴파일에서 걸린다.
 */
const DEFAULT_HINT: ProtocolHint = {
  host: '호스트 (QuickConnect ID 또는 DSM 주소)',
  hostPlaceholder: 'mynas.quickconnect.to',
  basePath: '시작 경로',
  basePathPlaceholder: '/',
  note: 'DSM FileStation Web API(HTTPS)로 붙습니다. 2FA 없는 전용 계정을 권장합니다.',
};

const HINTS: Record<string, ProtocolHint> = {
  SYNOLOGY_WEBAPI: DEFAULT_HINT,
  SMB: {
    host: '주소 (IP 또는 호스트명)',
    hostPlaceholder: '192.168.0.241',
    basePath: '시작 경로 — 첫 마디가 공유폴더 이름',
    basePathPlaceholder: '/부서공유',
    note: '탐색기에서 \\\\192.168.0.241\\부서공유 로 열리는 폴더입니다. file:// 주소를 그대로 붙여 넣어도 주소만 읽습니다. 계정이 없으면 비워 두세요(익명 접속).',
  },
  WEBDAV: {
    host: '호스트',
    hostPlaceholder: 'nas.example.com',
    basePath: '시작 경로',
    basePathPlaceholder: '/',
    note: '아직 지원하지 않습니다. 등록해 두어도 폴더가 열리지 않습니다.',
  },
};

/**
 * 이 패널이 다루는 갈래 — 현장 NAS 인가 본사(부서) NAS 인가.
 *
 * <p>부르는 화면({@code DocsNasPage})의 탭과 같은 값이다. 여기서 하는 일은 둘이다:
 * <b>목록을 그 갈래로 좁히고</b>, 새 연결을 만들 때 <b>소속 갈래를 미리 골라 둔다</b> —
 * 본사 탭에서 「연결 추가」를 눌렀는데 현장 선택이 먼저 뜨면 그것부터 되돌려야 한다.
 */
export type NasScope = 'SITE' | 'HQ';

/**
 * 이 갈래(현장/본사)의 연결만 남긴다.
 *
 * ⚠ `== null` 이다. 백엔드가 `non_null` 이라 비어 있는 필드는 JSON 에서 빠지므로, 현장 연결의
 * `departmentId` 는 `null` 이 아니라 <b>`undefined`</b> 로 온다(useDocuments 의 경고와 같은 자리).
 *
 * <p><b>소속이 통째로 없는 옛 행</b>(2026-08-24 이전)은 현장 쪽에 남긴다 — 그것이 전 직원에게 열려
 * 있다는 사실을 누군가 보고 고쳐야 하는데, 어느 탭에도 안 뜨면 아무도 못 본다.
 *
 * <p>부르는 화면이 <b>버튼의 개수·이상 표시</b>를 같은 규칙으로 세야 해서 함수로 뽑아 뒀다 —
 * 목록과 버튼이 서로 다른 수를 말하면 그 자체가 버그로 읽힌다.
 */
export function nasConnectionsOfScope<T extends { departmentId?: string | null }>(
  connections: T[],
  scope: NasScope,
): T[] {
  return connections.filter((connection) =>
    scope === 'HQ' ? connection.departmentId != null : connection.departmentId == null);
}

/**
 * NAS 연결 <b>한 벌</b>을 등록·수정·삭제하는 창.
 *
 * <h2>여는 자리는 하나 — 연결 판이다 (사용자 지시 2026-08-26)</h2>
 * 종전에는 갈래 탭 줄에 「⚙ 연결 관리」 버튼이 있었고, 그것이 <b>연결 목록 모달</b>을 열고 거기서
 * 다시 수정을 눌러 이 창에 닿았다. 그런데 그 목록은 왼쪽에서 나오는 <b>연결 판</b>과 같은 것을 한 번
 * 더 그린 것이었다 — 판의 카드마다 톱니바퀴가 생긴 뒤로는 같은 일을 두 자리에서 하고 있었다.
 *
 * <p>그래서 목록 모달과 그 버튼을 걷었다. 고치는 것도 <b>고르는 자리</b>에서 한다: 판의 톱니바퀴가
 * 이 창을 열고, 판 맨 아래의 「＋ 추가」 가 빈 창을 연다.
 *
 * @param scope 새 연결을 만들 때의 <b>기본 갈래</b>. 고치는 중이면 그 연결이 가진 갈래가 이긴다.
 * @param editing 고치는 연결. {@code null} 이면 새로 추가한다.
 */
export function NasConnectionEditor({
  scope,
  editing,
  onClose,
}: {
  scope: NasScope;
  editing: NasConnection | null;
  onClose: () => void;
}) {
  const { data: sites } = useQuery({
    queryKey: ['sites-min'],
    queryFn: () => apiFetch<Array<{ id: string; name: string }>>('/v1/sites'),
  });
  const { data: departments } = useQuery({
    queryKey: ['departments-min'],
    queryFn: () => apiFetch<Array<{ id: string; name: string }>>('/v1/departments'),
  });
  const write = useNasWrite();
  const test = useNasTest();

  const [form, setForm] = useState<NasConnectionInput>(() => seedForm(editing, scope));
  /**
   * <b>지금 편집 중인 연결</b>의 소속 갈래. 현장이냐 부서냐는 둘 중 하나이고(서버가 둘 다면 거절한다),
   * 값이 아니라 갈래가 먼저 정해져야 아래에 어떤 선택지를 낼지 정할 수 있다 — 값에서 거꾸로 읽으면
   * «부서를 고르기 전» 에는 부서 칸이 뜨지 않는다.
   *
   * <p>바깥 {@code scope}(보고 있는 탭)와 <b>다른 값</b>이다. 저것은 목록을 좁히고, 이것은 폼 한 벌을
   * 따라간다 — 수정을 눌렀을 때는 그 연결이 실제로 가진 갈래를 그대로 따라야 한다.
   */
  const [formScope, setFormScope] = useState<'SITE' | 'DEPARTMENT'>(() =>
    editing ? (editing.departmentId ? 'DEPARTMENT' : 'SITE') : scope === 'HQ' ? 'DEPARTMENT' : 'SITE',
  );

  const set = <K extends keyof NasConnectionInput>(key: K, value: NasConnectionInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  /** 갈래를 바꾸면 <b>고른 값도 함께 비운다</b> — 안 비우면 두 칸이 다 찬 채로 저장돼 서버가 거절한다. */
  const setScope = (next: 'SITE' | 'DEPARTMENT') => {
    setFormScope(next);
    setForm((prev) => ({ ...prev, siteId: null, departmentId: null }));
  };

  /*
    소속이 빠진 연결은 만들 수 없다 — 그것이 곧 "본사·공용" 으로 읽혀 전 직원에게 열린다.
    현장이냐 부서냐는 <b>둘 중 하나</b>다(서버도 NAS_SCOPE_AMBIGUOUS 로 막는다). 그래서 화면에서도
    두 칸을 나란히 두지 않고 **한 번 고른 갈래의 칸만** 보여 준다 — 나란히 두면 둘 다 채운 채로
    저장을 누르게 되고, 사람은 그것이 왜 거절인지 알 수 없다.
  */
  const scopePicked = !!form.siteId || !!form.departmentId;
  const canSubmit = form.name.trim() !== '' && form.host.trim() !== '' && scopePicked;
  const hint = HINTS[form.protocol] ?? DEFAULT_HINT;

  /**
   * <b>서버에 있는</b> 이 연결. 처음에는 고치러 들어온 그 줄이고, 추가 화면에서 「연결 테스트」 로
   * 한 번 저장하고 나면 <b>그때 돌아온 줄</b>이다 — 그 뒤로는 저장이 PUT 이 되고 삭제도 열린다.
   */
  const [saved, setSaved] = useState<NasConnection | null>(editing);
  const editId = saved?.id ?? null;

  const body = () => ({ ...form, password: form.password || null });

  const submit = () => {
    if (!canSubmit) return;
    write.mutate(
      { method: editId ? 'PUT' : 'POST', id: editId ?? undefined, body: body() },
      { onSuccess: onClose },
    );
  };

  /**
   * <b>저장한 뒤</b> 실제로 붙여 본다 (사용자 지시 2026-08-26).
   *
   * <p>시험은 id 로만 부를 수 있어서 <b>추가 화면에는 시험이 아예 없었다</b> — 주소·계정을 처음 넣는
   * 사람이야말로 「이게 맞나」 를 묻는데, 그 사람만 저장하고 창을 닫았다가 다시 열어야 했다.
   *
   * <p>고치는 중에도 먼저 저장한다. 화면의 값과 붙여 본 값이 <b>같아야</b> 결과가 뜻을 갖는다 —
   * 종전에는 「방금 고친 칸은 저장한 뒤에 반영됩니다」 라고 적어 두고 사람이 그것을 기억하게 했다.
   */
  const saveAndTest = () => {
    if (!canSubmit) return;
    write.mutate(
      { method: editId ? 'PUT' : 'POST', id: editId ?? undefined, body: body() },
      {
        onSuccess: (row) => {
          if (!row) return;
          setSaved(row);
          test.mutate(row.id);
        },
      },
    );
  };

  const remove = () => {
    if (!saved || !confirm(`"${saved.name}" 연결을 삭제할까요?`)) return;
    write.mutate({ method: 'DELETE', id: saved.id }, { onSuccess: onClose });
  };

  const trial = test.data?.id === editId ? test.data : null;
  const busy = write.isPending || test.isPending;

  return (
    <Modal
      title={editId ? 'NAS 연결 수정' : 'NAS 연결 추가'}
      // 안내(?)의 마지막 걸음이 이 창을 열어 놓고 짚는다(`pageGuides` 의 `/docs/nas`).
      tourKey="nas-editor"
      onClose={onClose}
      closeDisabled={write.isPending}
      footer={
        <div className="flex items-center gap-2 flex-wrap" style={{ width: '100%' }}>
          {/*
            <b>왼쪽 아래</b>가 「해 보는 것」 자리다(사용자 지시 2026-08-26) — 오른쪽은 창을 <b>끝내는</b>
            버튼(취소·저장)이고, 시험과 삭제는 창을 닫지 않는다. 지우는 것을 저장 옆에 두지 않는 이유도
            같다: 손이 미끄러지는 거리가 짧으면 안 된다.
          */}
          <button
            onClick={saveAndTest}
            className="btn-secondary"
            disabled={!canSubmit || busy}
            title="입력한 값을 저장한 뒤 실제로 접속해 봅니다."
          >
            {test.isPending ? '테스트 중…' : '연결 테스트'}
          </button>
          {saved && (
            <button onClick={remove} className="btn-danger-lg" disabled={busy} style={{ marginRight: 'auto' }}>
              삭제
            </button>
          )}
          <button onClick={onClose} className="btn-secondary" disabled={busy} style={saved ? undefined : { marginLeft: 'auto' }}>
            취소
          </button>
          <button onClick={submit} disabled={!canSubmit || busy} className="btn-primary">
            {editId ? '저장' : '추가'}
          </button>
        </div>
      }
    >
      {/*
        <b>붙여 본 결과</b>. 버튼은 왼쪽 아래에 있고 결과는 여기 맨 위에 남는다 — 사람이 다음에 고칠
        칸이 바로 아래이므로, 실패 사유는 그 칸들 위에 있어야 읽고 곧장 고친다.

        <p>추가 화면에도 뜬다: 「연결 테스트」 가 먼저 저장하기 때문이다({@link saveAndTest}).
      */}
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status={trial?.lastStatus ?? saved?.lastStatus ?? null} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {trial
            ? trial.lastStatus === 'OK'
              ? '연결 완료'
              : '연결 실패 — 다시 테스트해 주세요.'
            : '왼쪽 아래 「연결 테스트」 를 진행하세요.'}
        </span>
        {trial && (trial.lastCode || trial.lastDetail) && (
          <p className="basis-full" style={{ fontSize: 12, color: '#dc2626' }}>
            {trial.lastCode && <code style={{ marginRight: 6 }}>{trial.lastCode}</code>}
            {trial.lastDetail}
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {/* 필수 칸은 붉은 * 로만 표시한다 — 못 채우면 「추가·저장」 이 눌리지 않는다. */}
        <Field label={<>이름 <span style={{ color: '#dc2626' }}>*</span></>}><input className="input-field" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="○○현장 NAS" autoFocus /></Field>
        {/*
          소속은 **필수**다. 비워 두면 그 연결이 "본사·공용" 으로 읽혀 전 직원에게 열리고, 그 아래
          자료의 본문이 AI 검색에 그대로 뜬다(서버도 NAS_SCOPE_REQUIRED 로 막는다).

          <p>그 사정을 <b>칸 아래 설명 문단으로</b> 적어 뒀었는데, 두 칸짜리 격자의 왼쪽 칸에서
          줄이 넘쳐 옆 칸과 겹쳤다(사용자 지적 2026-08-26). <b>붉은 *</b> 하나면 필수라는 말은 다 한
          것이고, 못 채우면 「추가」 가 눌리지 않는다 — 설명은 그 다음에야 필요한 것이다.

          2026-08-25 부터 부서도 고를 수 있다. 종전에는 현장만 가능했고, 그래서 본사 NAS 는
          **등록할 길 자체가 없었다** — 좁힐 칸이 없어 통째로 금지돼 있었기 때문이다.
        */}
        {/*
          이 칸만 {@link Field} 를 쓰지 않는다. 「현장 / 본사 부서」 는 <b>이름표 줄 오른쪽</b>에 서고
          (사용자 지시 2026-08-26), 아래에는 드롭다운 하나만 남는다 — 세 줄이던 칸이 두 줄이 되어
          옆 칸(이름)과 높이가 맞는다. Field 의 이름표는 {@code <label>} 이라 그 안에 라디오를 넣으면
          이름표 속에 이름표가 겹친다.
        */}
        <div>
          {/* 이름표는 왼쪽, 고르는 것은 <b>오른쪽 끝</b> — 아래 드롭다운의 오른쪽 모서리와 줄이 맞는다. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              소속 <span style={{ color: '#dc2626' }}>*</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {(['SITE', 'DEPARTMENT'] as const).map((kind) => (
                <label
                  key={kind}
                  className="flex items-center gap-1 text-xs"
                  style={{ color: formScope === kind ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}
                >
                  <input type="radio" checked={formScope === kind} onChange={() => setScope(kind)} />
                  {kind === 'SITE' ? '현장' : '본사 부서'}
                </label>
              ))}
            </span>
          </div>
          {formScope === 'SITE' ? (
            <select
              className="input-field"
              value={form.siteId ?? ''}
              onChange={(e) => set('siteId', e.target.value || null)}
            >
              <option value="">현장을 고르세요</option>
              {(sites ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          ) : (
            <select
              className="input-field"
              value={form.departmentId ?? ''}
              onChange={(e) => set('departmentId', e.target.value || null)}
            >
              <option value="">부서를 고르세요</option>
              {(departments ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
        <Field label="프로토콜">
          <select className="input-field" value={form.protocol} onChange={(e) => set('protocol', e.target.value)}>
            {PROTOCOLS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{hint.note}</p>
        </Field>
        <Field label={<>{hint.host} <span style={{ color: '#dc2626' }}>*</span></>}><input className="input-field" value={form.host} onChange={(e) => set('host', e.target.value)} placeholder={hint.hostPlaceholder} /></Field>
        <Field label="포트(선택)"><input className="input-field" type="number" value={form.port ?? ''} onChange={(e) => set('port', e.target.value ? Number(e.target.value) : null)} placeholder={form.protocol === 'SMB' ? '445' : ''} /></Field>
        <Field label={hint.basePath}><input className="input-field" value={form.basePath} onChange={(e) => set('basePath', e.target.value)} placeholder={hint.basePathPlaceholder} /></Field>
        <Field label={form.protocol === 'SMB' ? '계정 (없으면 비워 두세요 — 익명 접속)' : '계정 (2FA 없는 전용 계정 권장)'}><input className="input-field" value={form.username ?? ''} onChange={(e) => set('username', e.target.value)} autoComplete="off" /></Field>
        <Field label={editId ? '비밀번호 (비우면 기존 유지)' : '비밀번호'}><input className="input-field" type="password" value={form.password ?? ''} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" /></Field>
        {/* 도메인은 SMB 에서만 뜻이 있다(AD·워크그룹 계정). Synology 쪽은 쓰지 않는 값이라
            늘 띄워 두면 «뭘 넣어야 하나» 를 매번 묻게 만든다. */}
        {form.protocol === 'SMB' && (
          <Field label="도메인/워크그룹 (선택)"><input className="input-field" value={form.domain ?? ''} onChange={(e) => set('domain', e.target.value || null)} placeholder="WORKGROUP" /></Field>
        )}
      </div>

      {/* 「AI 색인 기본 분류」 칸은 없앴다(2026-08-20). 등록하는 사람은 그 NAS 에 앞으로 무엇이
          올라올지 모르므로 대답할 수 없는 질문이었다. 못 정한 자료는 「99 미분류」로 들어가고,
          폴더별 분류는 자료조회 NAS 탭에서 폴더 내용을 보면서 걸 수 있다(선택). */}

      {/* 「읽기 전용」·「활성」 체크는 없앴다(2026-08-20). 앞은 자료실 → NAS 복제만 막았는데
          기본값이 켜짐이라 입력탭으로 넣은 파일이 전부 실패로 남았고, 뒤는 삭제와 일이 겹쳤다. */}

      {/* 관리자 계정(DOC-142) — 저장된 Synology 연결에만, SUPER_ADMIN 에게만. 창의 [저장] 과 따로 저장된다. */}
      {saved && form.protocol === 'SYNOLOGY_WEBAPI' && <NasAdminAccountSection connectionId={saved.id} />}

      {write.isError && <Notice tone="error">{(write.error as Error).message}</Notice>}
    </Modal>
  );
}

/**
 * 고칠 연결을 <b>폼 한 벌</b>로 눕힌다.
 *
 * <p>{@code ?? null} — 응답에서 빠진 필드(undefined)를 폼의 null 로 눕힌다. 백엔드가 null 필드를
 * JSON 에서 빼기 때문에(`jackson.default-property-inclusion: non_null`) 안 눕히면 select 의 value
 * 계산과 PUT 본문이 서로 다른 값을 보게 된다.
 *
 * <p>새 연결의 프로토콜 기본값은 <b>갈래를 따른다</b>(현장=Synology QuickConnect, 본사=사설망 SMB).
 */
function seedForm(editing: NasConnection | null, scope: NasScope): NasConnectionInput {
  if (!editing) return { ...EMPTY, protocol: scope === 'HQ' ? 'SMB' : 'SYNOLOGY_WEBAPI' };
  return {
    name: editing.name,
    siteId: editing.siteId ?? null,
    departmentId: editing.departmentId ?? null,
    protocol: editing.protocol,
    host: editing.host,
    port: editing.port,
    basePath: editing.basePath,
    username: editing.username ?? '',
    password: '',
    domain: editing.domain,
  };
}


function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs" style={{ color: 'var(--text-muted)' }}>미점검</span>;
  const ok = status === 'OK';
  return (
    <span className="text-xs px-1.5 py-0.5 rounded" style={{ color: ok ? '#16a34a' : '#dc2626', border: `1px solid ${ok ? '#16a34a' : '#dc2626'}` }}>
      {ok ? '정상' : status === 'AUTH_FAILED' ? '인증 실패' : '연결 실패'}
    </span>
  );
}
